/* S10 detail videos: autoplay visible demonstrations and defer offscreen media. */
(() => {
  const root = document.querySelector('#ac-product-details.ac-s10-layout');
  if (!root || root.dataset.acVideosReady) return;
  root.dataset.acVideosReady = 'true';
  const mobile = matchMedia('(max-width: 767px)');
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  const states = new Map();
  const players = new Map();
  let scheduled = 0;
  const allowAuto = () => !reducedMotion.matches && !connection?.saveData
    && !['slow-2g', '2g'].includes(connection?.effectiveType);
  const displayed = video => video.getClientRects().length > 0
    && getComputedStyle(video).visibility !== 'hidden';
  const playIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>';
  const pauseIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zm6 0h4v14h-4z" fill="currentColor"/></svg>';

  function paint(video, status) {
    const state = states.get(video);
    state.player.dataset.acVideoState = status;
    if (!state.button) return;
    const playing = status === 'playing';
    state.button.innerHTML = playing ? pauseIcon : playIcon;
    state.button.setAttribute('aria-label', status === 'error' ? 'Retry video' : playing ? 'Pause video' : 'Play video');
    state.button.title = status === 'error' ? 'Retry video' : playing ? 'Pause video' : 'Play video';
    state.button.hidden = mobile.matches || (video.controls && !['error', 'blocked', 'loading'].includes(status));
    if (mobile.matches) {
      video.tabIndex = 0;
      video.setAttribute('role', 'button');
      video.setAttribute('aria-label', state.button.getAttribute('aria-label'));
    } else {
      for (const [name, value] of state.originalControlAttributes) {
        if (value === null) video.removeAttribute(name);
        else video.setAttribute(name, value);
      }
    }
    state.status.textContent = status === 'error' ? 'Video unavailable. Tap to retry.'
      : status === 'blocked' ? 'Tap to play.' : '';
  }
  function pause(video) {
    const state = states.get(video);
    if (!video.paused) { state.expectedPause++; video.pause(); }
  }
  function prepare(video) {
    const state = states.get(video);
    if (!state.cover && video.dataset.acPoster && !video.poster) video.poster = mobile.matches ? (video.dataset.acPosterMobile || video.dataset.acPoster) : video.dataset.acPoster;
    const source = mobile.matches ? video.dataset.acMobile : video.dataset.acDesktop;
    if (!source) return false;
    if (video.dataset.acSelectedSource !== source) {
      state.resumeAt = video.currentTime || state.resumeAt;
      pause(video);
      video.preload = 'auto';
      video.src = source;
      video.dataset.acSelectedSource = source;
      video.load();
    }
    return true;
  }
  function release(video) {
    const state = states.get(video);
    if (state.inView || state.near || !video.getAttribute('src')) return;
    state.resumeAt = video.currentTime || state.resumeAt;
    state.request++;
    state.pending = false;
    pause(video);
    delete state.player.dataset.acHasFrame;
    video.removeAttribute('src');
    delete video.dataset.acSelectedSource;
    video.preload = 'none';
    video.load();
    paint(video, 'idle');
    schedule();
  }
  function start(video, user = false) {
    const state = states.get(video);
    if (user) {
      state.userStarted = true;
      state.userPaused = false;
      state.blocked = false;
      state.failed = false;
      if (video.error) { delete video.dataset.acSelectedSource; }
    }
    if (!displayed(video) || document.hidden || !prepare(video)) return;
    if (state.pending || !video.paused) return;
    state.pending = true;
    const request = ++state.request;
    paint(video, 'loading');
    const promise = video.play();
    promise?.then(() => {
      if (request !== state.request) return;
      state.pending = false;
      if (!state.inView || document.hidden || state.userPaused) pause(video);
    }).catch(error => {
      if (request !== state.request) return;
      state.pending = false;
      schedule();
      if (error.name === 'AbortError') return;
      state.blocked = error.name === 'NotAllowedError';
      state.failed = !state.blocked;
      video.controls = false;
      paint(video, state.blocked ? 'blocked' : 'error');
    });
  }
  function warmCover(state) {
    if (!state.cover) return;
    state.cover.fetchPriority = state.inView ? 'high' : 'low';
    state.cover.loading = 'eager';
  }
  function choose() {
    scheduled = 0;
    const candidates = [];
    states.forEach((state, video) => {
      if (document.hidden || !state.inView || !displayed(video)
        || state.userPaused || state.failed || state.blocked
        || (!state.userStarted && !allowAuto())) {
        pause(video);
        return;
      }
      warmCover(state);
      if (video.paused && !state.pending) candidates.push(video);
    });
    // Complete one mobile startup before opening the next media connection.
    if (mobile.matches && [...states.values()].some(s => s.inView && s.pending)) return;
    const distance = video => { const r = states.get(video).player.getBoundingClientRect(); return Math.abs(r.top + r.height / 2 - innerHeight / 2); };
    candidates.sort((a, b) => distance(a) - distance(b));
    for (const video of candidates) {
      const state = states.get(video);
      if (state.cover && !state.cover.complete && performance.now() - state.coverWait < 1800) {
        if (mobile.matches) break;
        continue;
      }
      start(video);
      if (mobile.matches && state.pending) break;
    }
  }
  function schedule() {
    // A continuous scroll must not keep postponing playback.
    if (!scheduled) scheduled = requestAnimationFrame(choose);
  }
  const covers = new IntersectionObserver(entries => entries.forEach(entry => {
    const video = players.get(entry.target);
    if (entry.isIntersecting && displayed(video)) warmCover(states.get(video));
  }), {rootMargin: '1400px 0px'});
  const near = new IntersectionObserver(entries => entries.forEach(entry => {
    const video = players.get(entry.target);
    const state = states.get(video);
    state.near = entry.isIntersecting && displayed(video);
    clearTimeout(state.releaseTimer);
    if (!state.near) state.releaseTimer = setTimeout(() => release(video), mobile.matches ? 1000 : 4000);
  }), {rootMargin: '360px 0px'});
  const visible = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const video = players.get(entry.target);
      const state = states.get(video);
      const inView = entry.isIntersecting && entry.intersectionRatio >= .25;
      if (inView && !state.inView) {
        state.coverWait = performance.now();
        clearTimeout(state.coverTimer);
        state.coverTimer = setTimeout(schedule, 1850);
      }
      state.inView = inView;
      if (state.cover) state.cover.fetchPriority = inView ? 'high' : 'low';
      if (!state.inView) pause(video);
    });
    schedule();
  }, {threshold: [0, .25, .5, .75, 1]});

  for (const video of root.querySelectorAll('video[data-ac-video]')) {
    const frame = video.closest('[data-pf-type="AcemateVideo"]');
    const player = video.closest('.pf-video-wrapper') || video.parentElement;
    player.classList.add('ac-s10-player');
    if (getComputedStyle(player).position === 'static') player.style.position = 'relative';
    (frame || player).querySelectorAll('.pf-video-cover-button, .pf-play-button, .pf-video-cover-image, .ac-s10-video-play').forEach(el => el.remove());
    const state = {player, cover: player.querySelector('.ac-s10-video-cover img'), coverWait: 0, coverTimer: 0,
      originalControlAttributes: ['role', 'tabindex', 'aria-label'].map(name => [name, video.getAttribute(name)]),
      near: false, inView: false, userStarted: false, userPaused: false, pending: false,
      blocked: false, failed: false, expectedPause: 0, request: 0, resumeAt: 0, releaseTimer: 0};
    states.set(video, state);
    players.set(player, video);
    if (state.cover) {
      state.cover.addEventListener('load', schedule);
      state.cover.addEventListener('error', schedule);
    }
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.controls = false;
    video.dataset.acAutoplay = 'true';
    video.loop = true;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ac-s10-video-toggle';
    state.button = button;
    const status = document.createElement('span');
    status.className = 'ac-s10-video-status';
    status.setAttribute('role', 'status');
    state.status = status;
    player.append(button, status);
    const togglePlayback = event => {
      event.preventDefault();
      event.stopPropagation();
      if ((!video.paused || state.pending) && !state.failed && !state.blocked) {
        state.userPaused = true;
        state.request++;
        state.pending = false;
        pause(video);
        schedule();
        paint(video, 'paused');
      } else start(video, true);
    };
    button.addEventListener('click', togglePlayback);
    video.addEventListener('click', event => {
      if (mobile.matches) togglePlayback(event);
    });
    video.addEventListener('keydown', event => {
      if (mobile.matches && !event.repeat && (event.key === 'Enter' || event.key === ' ')) togglePlayback(event);
    });
    video.addEventListener('loadedmetadata', () => {
      if (state.resumeAt > 0 && Number.isFinite(video.duration)) {
        video.currentTime = Math.min(state.resumeAt, Math.max(0, video.duration - .1));
        state.resumeAt = 0;
      }
    });
    video.addEventListener('playing', () => {
      if (!state.inView || document.hidden || state.userPaused) { pause(video); return; }
      state.pending = false;
      state.failed = false;
      state.blocked = false;
      state.player.dataset.acHasFrame = 'true';
      paint(video, 'playing');
      schedule();
    });
    video.addEventListener('pause', () => {
      if (state.expectedPause) state.expectedPause--;
      else if (state.inView && !video.ended) state.userPaused = true;
      if (video.paused && !state.failed && !state.blocked) paint(video, 'paused');
    });
    video.addEventListener('waiting', () => { if (state.inView && !video.paused) paint(video, 'loading'); });
    video.addEventListener('ended', () => { state.userPaused = true; state.userStarted = false; paint(video, 'paused'); });
    video.addEventListener('error', () => {
      if (!video.getAttribute('src')) return;
      state.failed = true;
      state.pending = false;
      video.controls = false;
      paint(video, 'error');
      schedule();
    });
    paint(video, 'idle');
    covers.observe(player);
    near.observe(player);
    visible.observe(player);
  }
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) states.forEach((_, video) => pause(video));
    else schedule();
  });
  window.addEventListener('scroll', schedule, {passive: true});
  window.addEventListener('resize', schedule, {passive: true});
  mobile.addEventListener('change', () => {
    states.forEach((state, video) => paint(video, state.player.dataset.acVideoState || 'idle'));
    schedule();
  });
  reducedMotion.addEventListener('change', schedule);
  connection?.addEventListener('change', schedule);
  window.addEventListener('pagehide', () => states.forEach((_, video) => pause(video)));
})();
